/**
 * Live monitoring: every in-scope broadcast with watch / force-stop, recent NG word
 * detections, open complaints and restricted streamers (requirement 2).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { ROUTES } from '../../lib/constants';
import { formatJstDateTime } from '../../lib/date-utils';
import type { ForceStopResponse, LiveStreamSummary, NgDetection, StreamerModerationState } from '../../lib/types';
import ReasonDialog, { type ReasonDialogResult } from './ReasonDialog';
import { NG_ACTION_LABELS, OME_OUTCOME_LABELS, SURFACE_LABELS } from './moderationLabels';

export const MONITOR_REFRESH_MS = 10_000;

interface LiveMonitorPanelProps {
  isAdmin: boolean;
}

type PendingAction =
  | { kind: 'stop'; stream: LiveStreamSummary }
  | { kind: 'liftBlock'; streamer: StreamerModerationState }
  | { kind: 'liftSuspension'; streamer: StreamerModerationState }
  | { kind: 'deleteChat'; detection: NgDetection };

const STATUS_LABELS: Record<LiveStreamSummary['broadcast_status'], string> = {
  live: '配信中',
  paused: '一時停止',
  suspended: '中断（再接続待ち）',
};

export default function LiveMonitorPanel({ isAdmin }: LiveMonitorPanelProps) {
  const [streams, setStreams] = useState<LiveStreamSummary[]>([]);
  const [detections, setDetections] = useState<NgDetection[]>([]);
  const [restricted, setRestricted] = useState<StreamerModerationState[]>([]);
  const [openReports, setOpenReports] = useState(0);
  const [overdueReports, setOverdueReports] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const load = useCallback(async () => {
    try {
      const [live, ngList, restrictedList, open, overdue] = await Promise.all([
        apiClient.listModerationLiveStreams(),
        apiClient.listNgDetections(10, 0),
        apiClient.listRestrictedStreamers(),
        apiClient.listContentReports(undefined, 1, 0, { openOnly: true }),
        apiClient.listContentReports(undefined, 1, 0, { overdueOnly: true }),
      ]);
      setStreams(live);
      setDetections(ngList.items);
      setRestricted(restrictedList);
      setOpenReports(open.total);
      setOverdueReports(overdue.total);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '監視情報の取得に失敗しました');
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => { void load(); }, MONITOR_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const describeStop = (result: ForceStopResponse) => {
    const failed = result.ome.filter((item) => item.outcome === 'failed' || item.outcome === 'not_configured');
    if (failed.length === 0) {
      setNotice({ tone: 'success', text: `${result.streamer_username} の配信を停止しました。解除するまで再配信できません。` });
    } else {
      setNotice({
        tone: 'warning',
        text: `${result.streamer_username} の配信を停止し、再配信を禁止しました。ただし${OME_OUTCOME_LABELS[failed[0].outcome]}。OMEの設定を確認してください。`,
      });
    }
  };

  const handleConfirm = async (input: ReasonDialogResult) => {
    if (!pending) return;
    if (pending.kind === 'stop') {
      describeStop(await apiClient.forceStopStream(pending.stream.stream_key, input));
    } else if (pending.kind === 'liftBlock') {
      await apiClient.liftPublishBlock(pending.streamer.streamer_id, input.reason);
      setNotice({ tone: 'success', text: `${pending.streamer.streamer_username} の配信停止を解除しました。` });
    } else if (pending.kind === 'liftSuspension') {
      await apiClient.liftStreamerSuspension(pending.streamer.streamer_id, input.reason);
      setNotice({ tone: 'success', text: `${pending.streamer.streamer_username} の利用停止を解除しました。` });
    } else {
      await apiClient.takedownChatMessage(Number(pending.detection.ref_id), input);
      setNotice({ tone: 'success', text: 'チャットメッセージを削除しました。' });
    }
    setPending(null);
    await load();
  };

  const dialogProps = (() => {
    if (!pending) return null;
    switch (pending.kind) {
      case 'stop':
        return {
          title: `${pending.stream.streamer_display_name || pending.stream.streamer_username} の配信を強制停止`,
          description: '視聴中の全員の再生を終了し、映像の入力を切断します。解除するまで同じ配信者は配信を開始できません。',
          confirmLabel: '強制停止する',
          withCategory: true,
        };
      case 'liftBlock':
        return { title: `${pending.streamer.streamer_username} の配信停止を解除`, confirmLabel: '解除する', withCategory: false };
      case 'liftSuspension':
        return { title: `${pending.streamer.streamer_username} の利用停止を解除`, confirmLabel: '解除する', withCategory: false };
      default:
        return {
          title: 'チャットメッセージを削除',
          description: `「${pending.detection.content_excerpt}」を視聴中の画面と履歴から削除します。`,
          confirmLabel: '削除する',
          withCategory: true,
        };
    }
  })();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold">配信監視</h3>
          <p className="text-xs text-gray-500">
            {MONITOR_REFRESH_MS / 1000}秒ごとに自動更新{updatedAt ? `（最終更新 ${updatedAt.toLocaleTimeString('ja-JP')}）` : ''}
          </p>
        </div>
        <div className="flex gap-2 text-sm">
          <span className="border border-gray-300 px-3 py-1">未対応の通報・苦情 <strong>{openReports}</strong> 件</span>
          <span className={`px-3 py-1 ${overdueReports > 0 ? 'bg-red-700 text-white' : 'border border-gray-300'}`}>
            期限超過 <strong>{overdueReports}</strong> 件
          </span>
        </div>
      </div>

      {error && <p className="bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}
      {notice && (
        <p className={`p-3 text-sm ${notice.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-900'}`} role="status">
          {notice.text}
        </p>
      )}

      <section>
        <h4 className="mb-2 font-semibold">配信中の配信（{streams.length}件）</h4>
        {streams.length === 0 ? (
          <p className="text-sm text-gray-500">現在配信中の配信はありません</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-2">配信者</th><th className="p-2">状態</th><th className="p-2">開始時刻</th>
                <th className="p-2">発言 / のぞき</th><th className="p-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {streams.map((stream) => (
                <tr key={stream.stream_key} className="border-b">
                  <td className="p-2">{stream.streamer_display_name || stream.streamer_username}<div className="text-xs text-gray-500">{stream.streamer_username}</div></td>
                  <td className="p-2">{STATUS_LABELS[stream.broadcast_status]}</td>
                  <td className="p-2">{formatJstDateTime(stream.started_at)}</td>
                  <td className="p-2">{stream.party_count} / {stream.peek_count}</td>
                  <td className="space-x-2 p-2">
                    <a href={ROUTES.WATCH_STREAMER(stream.streamer_username)} target="_blank" rel="noreferrer" className="text-blue-700 underline">視聴</a>
                    <button type="button" onClick={() => setPending({ kind: 'stop', stream })} className="bg-red-700 px-3 py-1 text-white">
                      強制停止
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h4 className="mb-2 font-semibold">停止中の配信者</h4>
        {restricted.length === 0 ? (
          <p className="text-sm text-gray-500">停止中の配信者はいません</p>
        ) : (
          <ul className="divide-y border text-sm">
            {restricted.map((streamer) => (
              <li key={streamer.streamer_id} className="flex flex-wrap items-center justify-between gap-2 p-2">
                <span>
                  {streamer.streamer_display_name || streamer.streamer_username}
                  {streamer.suspended_at && <span className="ml-2 bg-gray-800 px-2 py-0.5 text-xs text-white">利用停止 {formatJstDateTime(streamer.suspended_at)}</span>}
                  {streamer.publish_blocked_at && <span className="ml-2 bg-red-700 px-2 py-0.5 text-xs text-white">配信停止 {formatJstDateTime(streamer.publish_blocked_at)}</span>}
                  <span className="ml-2 text-xs text-gray-500">{streamer.suspension_reason || streamer.publish_block_reason}</span>
                </span>
                {isAdmin && (
                  <span className="space-x-2">
                    {streamer.publish_blocked_at && (
                      <button type="button" onClick={() => setPending({ kind: 'liftBlock', streamer })} className="border border-gray-400 px-2 py-1">配信停止を解除</button>
                    )}
                    {streamer.suspended_at && (
                      <button type="button" onClick={() => setPending({ kind: 'liftSuspension', streamer })} className="border border-gray-400 px-2 py-1">利用停止を解除</button>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="mb-2 font-semibold">直近のNGワード検知</h4>
        {detections.length === 0 ? (
          <p className="text-sm text-gray-500">検知はありません</p>
        ) : (
          <ul className="divide-y border text-sm">
            {detections.map((detection) => (
              <li key={detection.id} className="flex flex-wrap items-start justify-between gap-2 p-2">
                <span>
                  <span className={`mr-2 px-2 py-0.5 text-xs text-white ${detection.outcome === 'rejected' ? 'bg-red-700' : 'bg-yellow-600'}`}>
                    {detection.outcome === 'rejected' ? NG_ACTION_LABELS.block : NG_ACTION_LABELS.review}
                  </span>
                  {SURFACE_LABELS[detection.surface]}・{detection.matched_words.join('、')}
                  <span className="ml-2 text-gray-600">「{detection.content_excerpt}」</span>
                  <span className="ml-2 text-xs text-gray-500">
                    {detection.author_name ?? ''}{detection.target_streamer_username ? ` → ${detection.target_streamer_username}` : ''} {formatJstDateTime(detection.created_at)}
                  </span>
                </span>
                {detection.surface === 'chat' && detection.outcome === 'flagged' && detection.ref_id && /^\d+$/.test(detection.ref_id) && (
                  <button type="button" onClick={() => setPending({ kind: 'deleteChat', detection })} className="border border-red-700 px-2 py-1 text-red-700">
                    メッセージを削除
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {dialogProps && (
        <ReasonDialog open {...dialogProps} onCancel={() => setPending(null)} onConfirm={handleConfirm} />
      )}
    </div>
  );
}
